import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { 
  MapPin, 
  Plus, 
  Check, 
  CreditCard, 
  ArrowLeft,
  Truck,
  Shield,
  Loader2,
  CheckCircle,
  Tag,
  X,
  Printer,
  Wifi,
  Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - handles both relative and absolute URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  const [newAddress, setNewAddress] = useState({
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  // Load states on mount (doesn't require auth)
  useEffect(() => {
    const loadStates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/shop/states`);
        if (response.ok) {
          const data = await response.json();
          setStates(data);
          console.log('States loaded:', data.length);
        }
      } catch (error) {
        console.error('Error loading states:', error);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/shop');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const token = await user.getIdToken();
      
      // Load cart
      const cartRes = await fetch(`${API_URL}/api/shop/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        if (cartData.items.length === 0) {
          navigate('/shop');
          return;
        }
        setCart(cartData);
      }

      // Load addresses
      const addrRes = await fetch(`${API_URL}/api/shop/addresses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (addrRes.ok) {
        const addrData = await addrRes.json();
        setAddresses(addrData);
        // Select default address if available
        const defaultAddr = addrData.find(a => a.is_default);
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        } else if (addrData.length > 0) {
          setSelectedAddressId(addrData[0].id);
        }
      }

      // Load user profile for pre-filling address form
      const profileRes = await fetch(`${API_URL}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setUserProfile(profileData);
      }
    } catch (error) {
      console.error('Error loading checkout data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pre-fill address form when dialog opens
  const handleOpenAddressDialog = () => {
    setNewAddress({
      full_name: userProfile?.displayName || user?.displayName || '',
      phone: userProfile?.phoneNumber || '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      is_default: addresses.length === 0 // Set as default if first address
    });
    setShowAddressDialog(true);
  };

  const handleAddAddress = async () => {
    // Validation
    if (!newAddress.full_name || !newAddress.phone || !newAddress.address_line1 || 
        !newAddress.city || !newAddress.state || !newAddress.pincode) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    if (!/^\d{6}$/.test(newAddress.pincode)) {
      toast({
        title: "Invalid pincode",
        description: "Please enter a valid 6-digit pincode",
        variant: "destructive"
      });
      return;
    }

    if (!/^\d{10}$/.test(newAddress.phone)) {
      toast({
        title: "Invalid phone",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAddress)
      });

      if (response.ok) {
        const savedAddress = await response.json();
        setAddresses(prev => [...prev, savedAddress]);
        setSelectedAddressId(savedAddress.id);
        setShowAddressDialog(false);
        setNewAddress({
          full_name: '',
          phone: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          pincode: '',
          is_default: false
        });
        toast({
          title: "Address saved",
          description: "Your address has been added"
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save address');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Enter coupon code",
        description: "Please enter a valid coupon code",
        variant: "destructive"
      });
      return;
    }

    setCouponLoading(true);
    try {
      const token = await user.getIdToken();
      const productIds = cart.items.map(item => item.product?.id || item.product_id);
      
      const response = await fetch(`${API_URL}/api/shop/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          coupon_code: couponCode,
          product_ids: productIds,
          cart_total: cart.total
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid coupon');
      }

      const data = await response.json();
      setAppliedCoupon(data);
      toast({
        title: "Coupon Applied!",
        description: `${data.discount_percent}% off - You save ₹${data.discount_amount}`
      });
    } catch (error) {
      toast({
        title: "Invalid Coupon",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      toast({
        title: "No address selected",
        description: "Please select or add a delivery address",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);

    try {
      const token = await user.getIdToken();
      
      // Create order - this also creates Razorpay order
      console.log('Creating order with address:', selectedAddressId);
      const orderRes = await fetch(`${API_URL}/api/shop/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          address_id: selectedAddressId,
          payment_method: 'razorpay',
          coupon_code: appliedCoupon?.code || null
        })
      });

      if (!orderRes.ok) {
        const errorData = await orderRes.json();
        throw new Error(errorData.detail || 'Failed to create order');
      }

      const orderData = await orderRes.json();
      console.log('Order created:', orderData);

      // Load Razorpay script if not already loaded
      const loadRazorpay = () => {
        return new Promise((resolve, reject) => {
          if (window.Razorpay) {
            resolve();
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      };

      await loadRazorpay();
      
      // Small delay to ensure script is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Initialize Razorpay payment
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Piper Card',
        description: `Order #${orderData.order_number}`,
        order_id: orderData.razorpay_order_id,
        retry: {
          enabled: true,
          max_count: 3
        },
        handler: async function (response) {
          // Verify payment
          console.log('Payment response:', response);
          try {
            const verifyRes = await fetch(
              `${API_URL}/api/shop/orders/${orderData.id}/verify-payment`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                })
              }
            );

            const verifyData = await verifyRes.json();

            if (verifyRes.ok && verifyData.success) {
              setOrderId(orderData.order_number);
              setOrderSuccess(true);
            } else {
              throw new Error(verifyData.detail || 'Payment verification failed');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            toast({
              title: "Payment Verification Failed",
              description: "Payment received but verification failed. Please contact support.",
              variant: "destructive"
            });
          }
        },
        prefill: {
          email: user.email || '',
          contact: addresses.find(a => a.id === selectedAddressId)?.phone || ''
        },
        theme: {
          color: '#F59E0B'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            setProcessing(false);
            toast({
              title: "Payment Cancelled",
              description: "You can try again when ready",
            });
          },
          escape: true,
          backdropclose: false,
          confirm_close: false,
          animation: true
        }
      };

      console.log('Opening Razorpay...');
      const razorpayInstance = new window.Razorpay(options);
      
      razorpayInstance.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast({
          title: "Payment Failed",
          description: response.error.description || "Please try again",
          variant: "destructive"
        });
        setProcessing(false);
      });
      
      razorpayInstance.open();

    } catch (error) {
      console.error('Order error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process order. Please try again.",
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (orderSuccess) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed!</h2>
              <p className="text-gray-600 mb-4">
                Your order <span className="font-semibold">#{orderId}</span> has been placed successfully.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                You will receive an email confirmation shortly.
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  onClick={() => navigate('/orders')}
                >
                  View My Orders
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/shop')}
                >
                  Continue Shopping
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 md:py-8 px-3 sm:px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/cart')}
              className="text-xs sm:text-sm px-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Back to </span>Cart
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Checkout</h1>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-5 gap-4 sm:gap-6">
            {/* Left Column - Address & Payment */}
            <div className="lg:col-span-3 space-y-4 sm:space-y-6 order-2 lg:order-1">
              {/* Delivery Address */}
              <Card>
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                    Delivery Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {addresses.length === 0 ? (
                    <div className="text-center py-4 sm:py-6">
                      <p className="text-sm sm:text-base text-gray-500 mb-3 sm:mb-4">No saved addresses</p>
                      <Button onClick={handleOpenAddressDialog} className="text-sm sm:text-base">
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Address
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {addresses.map((address) => (
                        <div
                          key={address.id}
                          className={`p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                            selectedAddressId === address.id
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedAddressId(address.id)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm sm:text-base">{address.full_name}</p>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                                {address.address_line1}
                                {address.address_line2 && `, ${address.address_line2}`}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {address.city}, {address.state} - {address.pincode}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-500 mt-1">Phone: {address.phone}</p>
                            </div>
                            {selectedAddressId === address.id && (
                              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button 
                        variant="outline" 
                        className="w-full mt-2 sm:mt-3 text-sm"
                        onClick={handleOpenAddressDialog}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Address
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card>
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                    Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-3 sm:p-4 rounded-lg border-2 border-amber-500 bg-amber-50">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <img 
                          src="https://razorpay.com/favicon.png" 
                          alt="Razorpay" 
                          className="w-5 h-5 sm:w-6 sm:h-6"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base">Razorpay</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">UPI, Cards, Net Banking</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Next Steps - Show only for custom/uploaded designs */}
              {cart.items.some(i => i.item_type === 'custom_design' || i.item_type === 'uploaded_design') && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2 sm:pb-3">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-amber-800">
                      <Wifi className="w-4 h-4 sm:w-5 sm:h-5" />
                      Next Steps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-amber-900 leading-relaxed mb-3">
                      Our design team will contact you to collect any additional details needed for your cards (names, titles, contact info, etc.)
                    </p>
                    <div className="flex items-start gap-2 bg-white/60 rounded-lg p-3 border border-amber-200">
                      <span className="text-base">💡</span>
                      <p className="text-xs sm:text-sm text-amber-800">
                        <span className="font-medium">Tip:</span> For faster processing, upload a CSV file with all card details when adding items to cart.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Order Summary - Shows first on mobile */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <Card className="lg:sticky lg:top-4">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  {/* Items */}
                  <div className="space-y-2 sm:space-y-3">
                    {cart.items.map((item) => (
                      <div key={item.id} className="flex gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product?.images?.[0] ? (
                            <img 
                              src={resolveImageUrl(item.product.images[0])} 
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                            {item.product?.name}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-gray-900 flex-shrink-0">
                          {formatPrice(item.item_total)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-3 sm:pt-4 space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatPrice(cart.total)}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-green-600">Free</span>
                    </div>
                    {appliedCoupon && (
                      <div className="flex justify-between text-xs sm:text-sm text-green-600">
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          Coupon ({appliedCoupon.code})
                        </span>
                        <span>-{formatPrice(appliedCoupon.discount_amount)}</span>
                      </div>
                    )}
                  </div>

                  {/* Coupon Input */}
                  <div className="border-t pt-3 sm:pt-4">
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">{appliedCoupon.code}</span>
                          <span className="text-xs text-green-600">({appliedCoupon.discount_percent}% off)</span>
                        </div>
                        <button 
                          onClick={removeCoupon}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="flex-1 text-sm"
                        />
                        <Button
                          variant="outline"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="text-sm"
                        >
                          {couponLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Apply'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3 sm:pt-4">
                    <div className="flex justify-between">
                      <span className="font-semibold text-sm sm:text-base">Total</span>
                      <span className="font-bold text-lg sm:text-xl text-amber-600">
                        {formatPrice(appliedCoupon ? appliedCoupon.final_total : cart.total)}
                      </span>
                    </div>
                  </div>

                  {/* ── What you'll receive ── */}
                  {cart.items.length > 0 && (() => {
                    // Calculate quantities by item type
                    const designCardsQty = cart.items
                      .filter(i => i.item_type === 'custom_design' || i.item_type === 'uploaded_design')
                      .reduce((sum, i) => sum + (i.quantity || 1), 0);
                    
                    const readyMadeQty = cart.items
                      .filter(i => !i.item_type || i.item_type === 'product')
                      .reduce((sum, i) => sum + (i.quantity || 1), 0);
                    
                    const totalNfcCards = designCardsQty + readyMadeQty;
                    const totalPrintedCards = designCardsQty * 100;
                    
                    return (
                      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="checkout-what-youll-receive">
                        <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                          <Package className="w-3.5 h-3.5" />
                          What you'll receive
                        </p>
                        {designCardsQty > 0 && (
                          <div className="flex items-start gap-2">
                            <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-800">
                                {totalPrintedCards.toLocaleString()} Printed Visiting Cards
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {designCardsQty} design{designCardsQty > 1 ? 's' : ''} × 100 cards each · Premium paper
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-800">
                              {totalNfcCards} NFC Card{totalNfcCards > 1 ? 's' : ''} with QR
                            </p>
                            <p className="text-[10px] text-gray-500">Tap to share your digital profile</p>
                          </div>
                        </div>
                        <div className="pt-1 border-t border-emerald-200 flex items-center gap-1.5 text-[10px] text-emerald-700">
                          <Printer className="w-3 h-3" />
                          <span>All items shipped together · Free delivery</span>
                        </div>
                      </div>
                    );
                  })()}

                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm sm:text-base"
                    disabled={!selectedAddressId || processing}
                    onClick={handlePlaceOrder}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Place Order
                        <Shield className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-[10px] sm:text-xs text-gray-500">
                    <Truck className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Delivery within 5-7 business days</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Add Address Dialog */}
      <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Add New Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
            <div>
              <Label className="text-sm">Full Name *</Label>
              <Input
                value={newAddress.full_name}
                onChange={(e) => setNewAddress(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter full name"
                className="text-sm sm:text-base"
              />
            </div>
            <div>
              <Label className="text-sm">Phone Number *</Label>
              <Input
                value={newAddress.phone}
                onChange={(e) => setNewAddress(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="10-digit mobile number"
                className="text-sm sm:text-base"
              />
            </div>
            <div>
              <Label className="text-sm">Address Line 1 *</Label>
              <Input
                value={newAddress.address_line1}
                onChange={(e) => setNewAddress(prev => ({ ...prev, address_line1: e.target.value }))}
                placeholder="House/Flat No., Building, Street"
                className="text-sm sm:text-base"
              />
            </div>
            <div>
              <Label className="text-sm">Address Line 2</Label>
              <Input
                value={newAddress.address_line2}
                onChange={(e) => setNewAddress(prev => ({ ...prev, address_line2: e.target.value }))}
                placeholder="Area, Landmark (Optional)"
                className="text-sm sm:text-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">City *</Label>
                <Input
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  className="text-sm sm:text-base"
                />
              </div>
              <div>
                <Label className="text-sm">Pincode *</Label>
                <Input
                  value={newAddress.pincode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="6-digit"
                  className="text-sm sm:text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">State *</Label>
              <select
                value={newAddress.state}
                onChange={(e) => setNewAddress(prev => ({ ...prev, state: e.target.value }))}
                className="flex h-9 sm:h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={newAddress.is_default}
                onChange={(e) => setNewAddress(prev => ({ ...prev, is_default: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_default" className="text-xs sm:text-sm font-normal">
                Set as default address
              </Label>
            </div>
            <div className="flex gap-2 sm:gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 text-sm"
                onClick={() => setShowAddressDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-sm"
                onClick={handleAddAddress}
              >
                Save Address
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Checkout;
